import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState, useMemo } from "react";
import { Loader2, Play } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SmsHubLayout } from "@/components/sms/SmsHubLayout";
import { listCampaigns, listBatches, generateQueue, listQueue, countQueue } from "@/server/sms.functions";
import { useAuth } from "@/hooks/useAuth";


export const Route = createFileRoute("/dashboard/admin/sms/queue")({
  head: () => ({ meta: [{ title: "Queue · SMS" }, { name: "robots", content: "noindex" }] }),
  component: Page,
});

const STATUS_COLOR: Record<string, string> = {
  scheduled: "bg-blue-500/15 text-blue-700",
  queued: "bg-blue-500/15 text-blue-700",
  sending: "bg-amber-500/15 text-amber-700",
  sent: "bg-emerald-500/15 text-emerald-700",
  delivered: "bg-emerald-500/15 text-emerald-700",
  failed: "bg-rose-500/15 text-rose-700",
  undelivered: "bg-rose-500/15 text-rose-700",
  opted_out: "bg-rose-500/15 text-rose-700",
  suppressed: "bg-rose-500/15 text-rose-700",
  cancelled: "bg-muted",
};

function Page() {
  const { primaryRole } = useAuth();
  const qc = useQueryClient();
  const camps = useServerFn(listCampaigns);
  const batches = useServerFn(listBatches);
  const genQ = useServerFn(generateQueue);
  const listQ = useServerFn(listQueue);
  const countQ = useServerFn(countQueue);

  const { data: campaigns } = useQuery({ queryKey: ["sms-campaigns"], queryFn: () => camps(), enabled: primaryRole === "admin" });
  const { data: batchesData } = useQuery({ queryKey: ["sms-batches"], queryFn: () => batches(), enabled: primaryRole === "admin" });

  const [campaignId, setCampaignId] = useState<string>("");
  const [batchId, setBatchId] = useState<string>("");
  const [status, setStatus] = useState<string>("");

  const { data: queue, isLoading } = useQuery({
    queryKey: ["sms-queue", campaignId, status],
    queryFn: () => listQ({ data: { campaignId: campaignId || undefined, status: status || undefined } }),
    enabled: primaryRole === "admin",
  });

  const genMut = useMutation({
    mutationFn: () => genQ({ data: { campaignId, batchId } }),
    onSuccess: (r: any) => { toast.success(`Queued ${r.queued} (dry run)`); qc.invalidateQueries({ queryKey: ["sms-queue"] }); },
    onError: (e: any) => toast.error(e.message ?? "Failed"),
  });

  const PAGE_SIZE = 20;
  const {
    data: stagedPages,
    isLoading: stagedLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["sms-queue", "staged", "infinite"],
    enabled: primaryRole === "admin",
    initialPageParam: null as { createdAt: string; id: string } | null,
    queryFn: ({ pageParam }) =>
      listQ({ data: { status: "staged", limit: PAGE_SIZE + 1, cursor: pageParam ?? undefined } }),
    getNextPageParam: (lastPage: any[]) => {
      if (!lastPage || lastPage.length <= PAGE_SIZE) return undefined;
      const last = lastPage[PAGE_SIZE - 1];
      return { createdAt: last.created_at, id: last.id };
    },
  });
  const stagedRows = useMemo(
    () =>
      (stagedPages?.pages ?? []).flatMap((p: any[]) => (p?.length > PAGE_SIZE ? p.slice(0, PAGE_SIZE) : p ?? [])),
    [stagedPages],
  );
  const { data: stagedCountData } = useQuery({
    queryKey: ["sms-queue", "staged-count"],
    queryFn: () => countQ({ data: { status: "staged" } }),
    enabled: primaryRole === "admin",
    refetchInterval: 5000,
  });
  const stagedTotal = stagedCountData?.count ?? stagedRows.length;

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    const root = scrollRef.current;
    if (!el || !root) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root, rootMargin: "200px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);


  return (
    <SmsHubLayout title="Send Queue">
      <div className="card-surface p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-[13px] font-semibold font-display">Staged from matching</h3>
            <p className="text-[11px] text-muted-foreground">Providers matched by NPI, sitting in the queue waiting for you to go live.</p>
          </div>
          <div className="text-[12px]">
            <span className="font-semibold text-emerald-700">{stagedTotal.toLocaleString()}</span>{" "}
            <span className="text-muted-foreground">provider{stagedTotal === 1 ? "" : "s"} staged</span>
            {stagedRows.length > 0 && (
              <span className="text-muted-foreground"> · loaded {stagedRows.length.toLocaleString()}</span>
            )}
          </div>
        </div>
        <div ref={scrollRef} className="card-surface-flat max-h-[420px] overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Provider</TableHead>
                <TableHead>Practice</TableHead>
                <TableHead>NPI</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Staged</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stagedLoading && <TableRow><TableCell colSpan={6} className="text-center py-6"><Loader2 className="h-4 w-4 animate-spin inline" /></TableCell></TableRow>}
              {!stagedLoading && stagedRows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">No staged providers yet. Run matching on an uploaded batch.</TableCell></TableRow>}
              {stagedRows.map((r: any) => {
                const pid = r.providers?.id;
                const name = r.providers?.name ?? "—";
                const practice = r.providers?.practice ?? "—";
                return (
                  <TableRow key={r.id}>
                    <TableCell className="text-[12px] font-medium">
                      {pid ? (
                        <Link
                          to="/providers/$id"
                          params={{ id: String(pid) }}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          {name}
                        </Link>
                      ) : name}
                    </TableCell>
                    <TableCell className="text-[12px]">
                      {pid && r.providers?.practice ? (
                        <Link
                          to="/providers/$id"
                          params={{ id: String(pid) }}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-primary hover:underline"
                        >
                          {practice}
                        </Link>
                      ) : practice}
                    </TableCell>
                    <TableCell className="font-mono text-[11px]">{r.providers?.npi ?? "—"}</TableCell>
                    <TableCell className="font-mono text-[12px]">{r.recipient_phone}</TableCell>
                    <TableCell className="text-[12px]">{[r.providers?.city, r.providers?.state].filter(Boolean).join(", ") || "—"}</TableCell>
                    <TableCell className="text-[11px] text-muted-foreground whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</TableCell>
                  </TableRow>
                );
              })}
              {hasNextPage && (
                <TableRow>
                  <TableCell colSpan={6} className="py-3 text-center">
                    <div ref={sentinelRef} />
                    {isFetchingNextPage && <Loader2 className="h-4 w-4 animate-spin inline" />}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>



      <div className="card-surface p-4 mb-4">
        <h3 className="text-[13px] font-semibold mb-3 font-display">Generate dry-run queue</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="text-[11px] font-medium block mb-1">Campaign</label>
            <select value={campaignId} onChange={(e) => setCampaignId(e.target.value)} className="h-9 w-full border border-border rounded-md px-2 text-[13px] bg-background">
              <option value="">Select…</option>
              {(campaigns ?? []).map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-medium block mb-1">Source batch</label>
            <select value={batchId} onChange={(e) => setBatchId(e.target.value)} className="h-9 w-full border border-border rounded-md px-2 text-[13px] bg-background">
              <option value="">Select…</option>
              {(batchesData ?? []).map((b: any) => <option key={b.id} value={b.id}>{b.filename} ({b.matched_count} matched)</option>)}
            </select>
          </div>
          <Button onClick={() => genMut.mutate()} disabled={!campaignId || !batchId || genMut.isPending}>
            <Play className="h-3.5 w-3.5 mr-1.5" />{genMut.isPending ? "Generating…" : "Generate queue"}
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground mt-2">Dry run only — rows are scheduled but never sent. Distributes across enabled Twilio numbers with random jitter (10–25s) across the 12h window.</p>
      </div>

      <div className="flex gap-2 mb-3">
        <select value={campaignId} onChange={(e) => setCampaignId(e.target.value)} className="h-9 border border-border rounded-md px-2 text-[13px] bg-background">
          <option value="">All campaigns</option>
          {(campaigns ?? []).map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 border border-border rounded-md px-2 text-[13px] bg-background">
          <option value="">All statuses</option>
          {Object.keys(STATUS_COLOR).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="card-surface-flat">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Scheduled</TableHead>
              <TableHead>To</TableHead>
              <TableHead>From</TableHead>
              <TableHead>Body</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>SID / Error</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableRow><TableCell colSpan={6} className="text-center py-6"><Loader2 className="h-4 w-4 animate-spin inline" /></TableCell></TableRow>}
            {!isLoading && (queue ?? []).length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">No queue rows.</TableCell></TableRow>}
            {(queue ?? []).map((r: any) => (
              <TableRow key={r.id}>
                <TableCell className="text-[12px] whitespace-nowrap">{new Date(r.scheduled_at).toLocaleString()}</TableCell>
                <TableCell className="font-mono text-[12px]">{r.recipient_phone}</TableCell>
                <TableCell className="font-mono text-[12px]">{r.sms_twilio_numbers?.e164 ?? "—"}</TableCell>
                <TableCell className="text-[12px] max-w-[400px] truncate" title={r.rendered_body}>{r.rendered_body}</TableCell>
                <TableCell><span className={`text-[10.5px] px-2 py-0.5 rounded-md ${STATUS_COLOR[r.status] ?? "bg-muted"}`}>{r.status}{r.dry_run && " · dry"}</span></TableCell>
                <TableCell className="text-[11px] text-muted-foreground">{r.twilio_sid ?? r.error_code ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </SmsHubLayout>
  );
}
